/**
 * Landing page configuration stored in `listings.landing_config` (jsonb).
 * All fields are optional — when a listing has no config, pages render the
 * default hero/CTA layout unchanged.
 */

export type LandingButtonAction = "whatsapp" | "scroll_cta" | "url";

export interface LandingButton {
  label?: string;
  action?: LandingButtonAction;
  url?: string;
  color?: string;
}

export interface LandingHeroConfig {
  image_desktop?: string;
  image_mobile?: string;
  video_url?: string;
  background_image?: string;
  badge_text?: string;
  support_text?: string;
  above_button_text?: string;
  primary_button?: LandingButton;
  secondary_button?: LandingButton;
}

export interface LandingCtaConfig {
  title?: string;
  subtitle?: string;
  support_text?: string;
  image?: string;
  button?: LandingButton;
  background_color?: string;
  /** Custom WhatsApp message to prefill (overrides the default) */
  whatsapp_message?: string;
  /** Show inline lead-capture form under the CTA */
  show_form?: boolean;
  /** Display the phone number as a visible chip */
  show_phone?: boolean;
  /** Show a "Ligar" (tel:) button */
  show_call_button?: boolean;
  /** Show a "Tour Virtual" button */
  show_tour_button?: boolean;
  /** URL for the virtual tour (when show_tour_button is true) */
  tour_url?: string;
}

export interface LandingConfig {
  hero?: LandingHeroConfig;
  cta?: LandingCtaConfig;
}

export const EMPTY_LANDING_CONFIG: LandingConfig = { hero: {}, cta: {} };

/** True when any hero field is filled in — controls whether the custom hero renders. */
export function hasHero(cfg?: LandingConfig | null): boolean {
  const h = cfg?.hero;
  if (!h) return false;
  return Boolean(
    h.image_desktop || h.image_mobile || h.video_url || h.background_image ||
    h.badge_text || h.support_text || h.above_button_text ||
    h.primary_button?.label || h.secondary_button?.label
  );
}

/** True when any CTA field is filled in — controls whether the CTA block renders. */
export function hasCta(cfg?: LandingConfig | null): boolean {
  const c = cfg?.cta;
  if (!c) return false;
  return Boolean(c.title || c.subtitle || c.support_text || c.image || c.button?.label);
}

/** Extract a YouTube video id from any common URL shape. */
export function parseYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
  return m?.[1] || null;
}

/** Extract a Vimeo video id (numeric) from any common URL shape. */
export function parseVimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m?.[1] || null;
}
